import React from 'react';
import styled from 'styled-components';
import IconBlock from '@component/common/block/icon-block';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import { ETheme, TIcon } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

type TCollectionPlaceholderSize = 'small' | 'medium' | 'large';

interface IProps {
  theme?: ETheme;
  size?: TCollectionPlaceholderSize;
  width?: string;
  height?: string;
  icon: TIcon;
  title: string;
  description?: string;
  buttonText?: string;
  buttonIcon?: TIcon;
  handleButtonClick?: () => void;
}

const createDesign = (placeholderSize: TCollectionPlaceholderSize, theme: ETheme, width?: string, height?: string) => {
  const createSize = () => {
    switch (placeholderSize) {
      case 'small':
        return { padding: size.MARGIN_2, titleTextSize: 'medium' as const, descriptionTextSize: 'small' as const, iconSize: 'medium' as const };
      case 'large':
        return { padding: size.MARGIN_4, titleTextSize: 'large' as const, descriptionTextSize: 'large' as const, iconSize: 'large' as const };
      default:
        return { padding: size.MARGIN_3, titleTextSize: 'large' as const, descriptionTextSize: 'medium' as const, iconSize: 'large' as const };
    }
  };

  const createTheme = () => {
    const iconColors: Partial<Record<ETheme, string>> = {
      [ETheme.BROWN]: color.BROWN_2,
      [ETheme.CYAN]: color.CYAN_2,
      [ETheme.BLUE]: color.BLUE_2,
      [ETheme.GREEN]: color.GREEN_3,
      [ETheme.RED]: color.RED_3,
      [ETheme.YELLOW]: color.YELLOW_1,
      [ETheme.GRAY]: color.GRAY_1,
      [ETheme.BLACK]: color.BLACK_2,
    };
    return {
      backgroundColor: color.WHITE_1,
      titleColor: color.BLACK_2,
      descriptionTextColor: color.GRAY_1,
      borderColor: color.GRAY_2,
      iconColor: iconColors[theme] ?? color.BROWN_2,
    };
  };

  const createEvent = () => ({ cursor: 'default' as const });

  return {
    width,
    height,
    ...createSize(),
    ...createTheme(),
    borderWidth: size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH,
    borderRadius: size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS,
    gap: size.MARGIN_1,
    textGap: '5px',
    buttonWidth: '150px',
    event: createEvent(),
  };
};

const Wrapper = styled.div<{ $design: ReturnType<typeof createDesign> }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: min-content;
  box-sizing: border-box;
  width: ${(props) => props.$design.width || 'fit-content'};
  height: ${(props) => props.$design.height || 'auto'};
  padding: ${(props) => props.$design.padding};
  background-color: ${(props) => props.$design.backgroundColor};
  border: ${(props) => props.$design.borderWidth} solid ${(props) => props.$design.borderColor};
  border-radius: ${(props) => props.$design.borderRadius};
  gap: ${(props) => props.$design.gap};
  cursor: ${(props) => props.$design.event.cursor};
`;

const TextContainer = styled.div<{ $design: ReturnType<typeof createDesign> }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.$design.textGap};
`;

const CollectionPlaceholder: React.FC<IProps> = ({
  theme = ETheme.BROWN,
  size = 'medium',
  width,
  height,
  icon,
  title,
  description,
  buttonText,
  buttonIcon,
  handleButtonClick,
}) => {
  const design = createDesign(size, theme, width, height);

  return (
    <Wrapper $design={design}>
      <IconBlock icon={icon} design={{ icon: { size: design.iconSize, color: design.iconColor } }} />
      <TextContainer $design={design}>
        <TextBlock text={title} design={{ text: { size: design.titleTextSize, color: design.titleColor, bold: true } }} />
        {description && <TextBlock text={description} design={{ text: { size: design.descriptionTextSize, color: design.descriptionTextColor } }} />}
      </TextContainer>
      {buttonText && (
        <TextButton
          text={buttonText}
          icon={buttonIcon}
          arrangement='icon-text'
          theme={theme}
          kind='filled'
          size={size}
          width={design.buttonWidth}
          shape='pill'
          handleClick={handleButtonClick}
        />
      )}
    </Wrapper>
  );
};

export default CollectionPlaceholder;
